import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 * The video only starts downloading when it gets close to the viewport,
 * and pauses while it is scrolled out of view.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  /** @type {IntersectionObserver | undefined} */
  #observer;
  #loaded = false;

  connectedCallback() {
    super.connectedCallback();

    if (!('IntersectionObserver' in window)) {
      this.#load();
      return;
    }

    this.#observer = new IntersectionObserver(this.#handleIntersection, { rootMargin: '300px 0px' });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #handleIntersection = (entries) => {
    const { videoElement } = this.refs;

    for (const entry of entries) {
      if (entry.isIntersecting) {
        if (!this.#loaded) this.#load();
        else videoElement.play().catch(() => {});
      } else if (this.#loaded) {
        videoElement.pause();
      }
    }
  };

  #load() {
    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();
    this.#loaded = true;
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
